import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginPage } from './login-page';
import { RegisterPage } from './register-page';
import { VerifyEmailModal } from '@widgets/auth/verify-email-modal';
import { TwoFactorModal } from '@widgets/auth/two-factor-modal';
import { useAuth } from '@entities/auth';
import { resolveAuthReturnPath } from '@shared/lib/auth-return-path';
import { AuthBootstrapSkeleton } from '@shared/ui';

type AuthView = 'login' | 'register';

export function AuthPage() {
  const [view, setView] = useState<AuthView>('login');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [skipInitialEmailSend, setSkipInitialEmailSend] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      const admin = user?.isAdmin === true || user?.role === 'ADMIN';
      navigate(resolveAuthReturnPath(location.state, admin ? '/workspace' : '/balance'), { replace: true });
    }
  }, [isAuthenticated, isLoading, location.state, navigate, user]);

  if (isLoading) {
    return <AuthBootstrapSkeleton />;
  }

  const handleNeedEmailVerification = (email?: string, emailAlreadySent = false) => {
    if (email) setUserEmail(email);
    setSkipInitialEmailSend(emailAlreadySent);
    setShowEmailVerification(true);
  };

  const handleCloseEmailVerification = () => {
    setShowEmailVerification(false);
    setSkipInitialEmailSend(false);
  };

  const handleNeed2FA = () => {
    setShow2FA(true);
  };

  return (
    <>
      {view === 'login' ? (
        <LoginPage
          onNeedEmailVerification={handleNeedEmailVerification}
          onNeed2FA={handleNeed2FA}
          onSwitchToRegister={() => setView('register')}
        />
      ) : (
        <RegisterPage
          onNeedEmailVerification={(email) => handleNeedEmailVerification(email, true)}
          onSwitchToLogin={() => setView('login')}
        />
      )}

      <VerifyEmailModal
        open={showEmailVerification}
        onClose={handleCloseEmailVerification}
        email={userEmail}
        skipInitialSend={skipInitialEmailSend}
      />

      <TwoFactorModal
        open={show2FA}
        onClose={() => setShow2FA(false)}
      />
    </>
  );
}
